import React, { useContext, useEffect, useState } from 'react'
import { BigNumber, ethers } from 'ethers'
import styled from 'styled-components'
import { Button } from 'antd'

import { Web3Button, Web3Modal } from '@web3modal/react'

import { useContract, useSigner } from 'wagmi'

import { formatUnits } from 'ethers/lib/utils'
import { useHistory } from 'react-router-dom'
import { Step } from './components/Step'
import { FileUpload } from './components/FileUpload'
import { PanelWrapper, WalletInfoWrapper } from './components/ConnectWallet'
import { INetwork, NETWORKS } from '../../helpers/chains'
import { useTokenBalance } from '../../hooks/useTokenBalance'
import { ethereumClient, useWallet } from '../../hooks/useWallet'
import { AuthContext } from '../../context/AuthProvider'
import { ERC677ABI } from '../../abis/erc677'
import { useTokenInfo } from '../../hooks/useTokenInfo'
import { Loading } from './components/Loading'
import { ValidatorCreated } from './components/ValidatorCreated'
import { openErrorNotificationWithIcon, updateLSItem } from '../../helpers/utilities'

const depositAmountBN = BigNumber.from(1).mul(
  BigNumber.from(ethers.constants.WeiPerEther)
)

export const Create = () => {
  const [isLoading, setLoading] = useState(false)
  const [tx, setTx] = useState(null)
  const [error, setError] = useState(null)
  const [isValidatorCreated, setValidatorCreated] = useState(null)
  const history = useHistory()

  const { data: signer } = useSigner()
  const { wallet, address, chain } = useWallet()
  useEffect(() => {
    if (chain?.id === 5) {
      history.push('/gno-bridge')
    }
  }, [chain])
  // @ts-ignore
  const network: INetwork = wallet ? NETWORKS[wallet.chainId] : null
  // @ts-ignore
  const fallback: INetwork = NETWORKS[10200] // chiado
  // @ts-ignore
  const t: string = network ? network.addresses.token : fallback.addresses.token

  const { tokenInfo, contract } = useTokenInfo(t)
  const token = useContract({
    address: tokenInfo.address,
    abi: ERC677ABI,
    signerOrProvider: signer,
  })
  const { transferData, setTransferData, setUserDataToContext } =
    useContext(AuthContext)

  const tokenBalance = useTokenBalance(address, contract)

  useEffect(() => {
    if (chain) {
      setUserDataToContext({ address, chainId: chain.id })
    }
  }, [address])

  const resetState = () => {
    setLoading(false)
    setError(null)
    setTx(null)
    setValidatorCreated(false)
    setTransferData(null)
  }

  const onCreate = async () => {
    setLoading(true)
    let currentTx
    await Promise.all(
      transferData.map(async (deposit: IFileContent) => {
        let data = '0x'
        data += deposit.withdrawal_credentials
        data += deposit.pubkey
        data += deposit.signature
        data += deposit.deposit_data_root

        try {
          currentTx = await token.transferAndCall(
            network.addresses.deposit,
            depositAmountBN.mul(BigNumber.from(1)),
            data
          )
          setTx(currentTx)
        } catch (err) {
          // eslint-disable-next-line no-unused-expressions
          err.reason
            ? openErrorNotificationWithIcon(err.reason)
            : openErrorNotificationWithIcon(err.message)
        }
        return tx
      })
    )

    try {
      // @ts-ignore
      const txInfo = await currentTx.wait()
      setLoading(false)
      setTx(txInfo)
      updateLSItem(`${address}-${wallet.chainId}`, {
        pubkeys: transferData[0].pubkey,
        transactionHash: txInfo.transactionHash,
      })
      setValidatorCreated(txInfo)

      setLoading(false)
    } catch (e) {
      setError(e.message)
      setLoading(false)
    }
  }
  if (error) {
    return (
      <AppContainer>
        <Loading
          tx={tx}
          error={error}
          resetState={resetState}
          errorText="Unable to create validators"
        />
      </AppContainer>
    )
  }
  if (isLoading) {
    return (
      <AppContainer>
        <Loading tx={tx} loadingText="Creating validators..." />
      </AppContainer>
    )
  }
  if (isValidatorCreated) {
    return (
      <AppContainer>
        <ValidatorCreated tx={tx} resetState={resetState} />
      </AppContainer>
    )
  }

  return (
    <AppContainer>
      {!tokenInfo ? (
        'loading'
      ) : (
        <CreateWrapper>
          <Step title="Step 1 of 2: Connect your wallet">
            <>
              <PanelWrapper>
                {address && (
                  <WalletInfoWrapper>
                    <p>{chain.name}</p>
                    <p>{address}</p>
                    {tokenInfo?.decimals && tokenBalance && (
                      <p>
                        Balance:{' '}
                        {Number(formatUnits(tokenBalance, tokenInfo.decimals))}{' '}
                        {tokenInfo.symbol}
                      </p>
                    )}
                  </WalletInfoWrapper>
                )}
                <Web3Button />
              </PanelWrapper>

              <Web3Modal
                projectId="9a2846ab319d3e074b744395e11c7fae"
                ethereumClient={ethereumClient}
              />
            </>
          </Step>
          {address && (
            <>
              <Step title="Step 2 of 2: Upload configuration file">
                <FileUpload chain={chain} tokenInfo={tokenInfo} />
              </Step>
              <Button
                style={{ width: 564 }}
                type="primary"
                onClick={onCreate}
                disabled={!transferData}
              >
                Create validators
              </Button>
            </>
          )}
        </CreateWrapper>
      )}
    </AppContainer>
  )
}

const AppContainer = styled.div`
  height: calc(100vh - 134px);
  display: flex;
  align-items: center;
  justify-content: center;
`
const CreateWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  .ant-collapse-header-text {
    font-weight: bold;
  }
`

export interface IFileContent {
  pubkey: string
  signature: string
  deposit_data_root: string
  withdrawal_credentials: string
}
